import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TextField,
  MenuItem,
  Button,
  CircularProgress,
  Alert,
  Rating,
  Divider,
  Stack
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import api from '../services/api';

// Types
interface CSATStatistics {
  total_responses: number;
  average_rating: number;
  rating_distribution: { [key: string]: number };
  satisfaction_rate: number;
  response_rate: number;
}

interface CSATResponseDetail {
  id: number;
  conversation_id: number;
  contact_id: number | null;
  contact_name: string | null;
  contact_email: string | null;
  channel_id: number;
  rating: number;
  feedback: string | null;
  submitted_at: string;
}

interface CSATAnalytics {
  statistics: CSATStatistics;
  responses: CSATResponseDetail[];
  total_count: number;
}

// Color mapping for ratings
const RATING_COLORS = {
  '1': '#f44336', // Red
  '2': '#ff9800', // Orange
  '3': '#ffc107', // Amber
  '4': '#8bc34a', // Light Green
  '5': '#4caf50'  // Green
};

const RATING_LABELS = {
  1: 'Very Dissatisfied',
  2: 'Dissatisfied',
  3: 'Neutral',
  4: 'Satisfied',
  5: 'Very Satisfied'
};

const CSATAnalyticsPage: React.FC = () => {
  const [analytics, setAnalytics] = useState<CSATAnalytics | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [startDate, setStartDate] = useState<Date | null>(
    new Date(Date.now() - 30 * 24 * 60 * 60 * 1000) // Last 30 days
  );
  const [endDate, setEndDate] = useState<Date | null>(new Date());
  const [selectedChannel, setSelectedChannel] = useState<number | ''>('');
  const [selectedRating, setSelectedRating] = useState<number | ''>('');

  // Fetch analytics data
  const fetchAnalytics = async () => {
    setLoading(true);
    setError(null);

    try {
      // Get current user's account ID (you'll need to get this from your auth context)
      const accountId = 1; // TODO: Get from auth context

      const params: any = {};
      if (selectedChannel) params.channel_id = selectedChannel;
      if (startDate) params.start_date = startDate.toISOString();
      if (endDate) params.end_date = endDate.toISOString();

      const response = await api.get<CSATAnalytics>(
        `/accounts/${accountId}/csat/analytics`,
        { params }
      );

      setAnalytics(response.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load CSAT analytics');
      console.error('Error fetching CSAT analytics:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, []);

  // Prepare chart data
  const ratingDistributionData = analytics
    ? Object.entries(analytics.statistics.rating_distribution).map(([rating, count]) => ({
        rating: `${rating} ⭐`,
        count,
        fill: RATING_COLORS[rating as keyof typeof RATING_COLORS]
      }))
    : [];

  const satisfactionData = analytics
    ? [
        {
          name: 'Satisfied (4-5)',
          value: (analytics.statistics.rating_distribution['4'] || 0) +
                 (analytics.statistics.rating_distribution['5'] || 0),
          fill: '#4caf50'
        },
        {
          name: 'Neutral (3)',
          value: analytics.statistics.rating_distribution['3'] || 0,
          fill: '#ffc107'
        },
        {
          name: 'Dissatisfied (1-2)',
          value: (analytics.statistics.rating_distribution['1'] || 0) +
                 (analytics.statistics.rating_distribution['2'] || 0),
          fill: '#f44336'
        }
      ]
    : [];

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getRatingColor = (rating: number): string => {
    return RATING_COLORS[rating.toString() as keyof typeof RATING_COLORS] || '#999';
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" gutterBottom>
            CSAT Analytics
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Customer Satisfaction Survey Results and Analytics
          </Typography>
        </Box>

        {/* Filters */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Filters
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                select
                fullWidth
                label="Channel"
                value={selectedChannel}
                onChange={(e) => setSelectedChannel(e.target.value as number | '')}
              >
                <MenuItem value="">All Channels</MenuItem>
                <MenuItem value={1}>Channel 1</MenuItem>
                <MenuItem value={2}>Channel 2</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                fullWidth
                variant="contained"
                onClick={fetchAnalytics}
                sx={{ height: '56px' }}
              >
                Apply Filters
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {analytics && (
          <>
            {/* Statistics Cards */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom>
                      Total Responses
                    </Typography>
                    <Typography variant="h4">
                      {analytics.statistics.total_responses}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom>
                      Average Rating
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="h4">
                        {analytics.statistics.average_rating.toFixed(2)}
                      </Typography>
                      <Rating
                        value={analytics.statistics.average_rating}
                        readOnly
                        precision={0.1}
                        size="small"
                      />
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom>
                      Satisfaction Rate
                    </Typography>
                    <Typography variant="h4" color="success.main">
                      {analytics.statistics.satisfaction_rate.toFixed(1)}%
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      (4-5 star ratings)
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="text.secondary" gutterBottom>
                      Response Rate
                    </Typography>
                    <Typography variant="h4">
                      {analytics.statistics.response_rate.toFixed(1)}%
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Charts */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              {/* Rating Distribution Bar Chart */}
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Rating Distribution
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={ratingDistributionData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="rating" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="count" name="Responses" />
                    </BarChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>

              {/* Satisfaction Pie Chart */}
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Satisfaction Overview
                  </Typography>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={satisfactionData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={(entry) => `${entry.name}: ${entry.value}`}
                        outerRadius={80}
                        dataKey="value"
                      >
                        {satisfactionData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </Paper>
              </Grid>
            </Grid>

            {/* Recent Responses Table */}
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Recent Responses ({analytics.total_count} total)
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Contact</TableCell>
                      <TableCell>Rating</TableCell>
                      <TableCell>Feedback</TableCell>
                      <TableCell>Conversation</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {analytics.responses.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          <Typography color="text.secondary">
                            No CSAT responses found for the selected period
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      analytics.responses.map((response) => (
                        <TableRow key={response.id}>
                          <TableCell>
                            <Typography variant="body2">
                              {formatDate(response.submitted_at)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {response.contact_name || 'Anonymous'}
                            </Typography>
                            {response.contact_email && (
                              <Typography variant="caption" color="text.secondary">
                                {response.contact_email}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Chip
                                label={response.rating}
                                size="small"
                                sx={{
                                  bgcolor: getRatingColor(response.rating),
                                  color: 'white',
                                  fontWeight: 'bold'
                                }}
                              />
                              <Typography variant="caption">
                                {RATING_LABELS[response.rating as keyof typeof RATING_LABELS]}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{
                                maxWidth: 300,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {response.feedback || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => {
                                // Navigate to conversation
                                window.location.href = `/conversations/${response.conversation_id}`;
                              }}
                            >
                              View
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </>
        )}
      </Container>
    </LocalizationProvider>
  );
};

export default CSATAnalyticsPage;
